import { PropsWithChildren, createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

export type NotificationPayload = {
  id?: string;
  type: 'info' | 'warning' | 'error';
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  autoClose?: boolean;
  duration?: number;
};

type NotificationEntry = NotificationPayload & {
  id: string;
};

type NotificationContextValue = {
  notify: (payload: NotificationPayload) => string;
  dismiss: (id: string) => void;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const iconLookup = {
  error: ExclamationTriangleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
} as const;

const paletteLookup = {
  error: {
    container: 'border-status-error/40 bg-status-error/10 text-status-error',
    accent: 'text-status-error',
    icon: 'text-status-error',
  },
  warning: {
    container: 'border-status-warning/40 bg-status-warning/10 text-status-warning',
    accent: 'text-status-warning',
    icon: 'text-status-warning',
  },
  info: {
    container: 'border-status-info/30 bg-surface-secondary text-text-primary',
    accent: 'text-status-info',
    icon: 'text-status-info',
  },
} as const;

const NotificationCard = ({ notification, onDismiss }: { notification: NotificationEntry; onDismiss: () => void }) => {
  const palette = paletteLookup[notification.type];
  const Icon = iconLookup[notification.type];

  return (
    <div
      className={clsx(
        'pointer-events-auto overflow-hidden rounded-2xl border px-4 py-3 shadow-xl shadow-shadow-soft/40 backdrop-blur',
        palette.container,
      )}
      role="status"
      aria-live={notification.type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="flex items-start gap-3">
        <Icon className={clsx('h-5 w-5', palette.icon)} aria-hidden />
        <div className="flex-1 space-y-1 text-sm">
          <p className="font-semibold leading-tight">{notification.title}</p>
          {notification.message ? (
            <p className={clsx('text-xs leading-snug', palette.accent)}>{notification.message}</p>
          ) : null}
          {notification.actionLabel ? (
            <button
              type="button"
              className="text-xs font-semibold text-status-info underline-offset-2 hover:underline"
              onClick={() => {
                notification.onAction?.();
                onDismiss();
              }}
            >
              {notification.actionLabel}
            </button>
          ) : null}
        </div>
                  <button
                    type="button"
                    onClick={onDismiss}
                    className="rounded-full p-1 text-xs text-text-muted-strong hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-surface-secondary"
                    aria-label="Dismiss notification"
                  >          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const NotificationProvider = ({ children }: PropsWithChildren) => {
  const [notifications, setNotifications] = useState<NotificationEntry[]>([]);
  const timeoutMap = useRef<Record<string, number>>({});

  const clearTimer = useCallback((id: string) => {
    const timeoutId = timeoutMap.current[id];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      delete timeoutMap.current[id];
    }
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setNotifications((previous) => previous.filter((note) => note.id !== id));
    },
    [clearTimer],
  );

  const notify = useCallback(
    (payload: NotificationPayload) => {
      const id = payload.id ?? randomId();
      const entry: NotificationEntry = { ...payload, id };
      setNotifications((previous) => {
        const next = [...previous, entry];
        const overflow = next.length - 3;
        if (overflow > 0) {
          const removed = next.slice(0, overflow);
          removed.forEach((note) => clearTimer(note.id));
          return next.slice(overflow);
        }
        return next;
      });

      const shouldAutoClose = payload.autoClose ?? payload.type !== 'error';
      if (shouldAutoClose) {
        const timeoutId = window.setTimeout(() => dismiss(id), payload.duration ?? 6000);
        timeoutMap.current[id] = timeoutId;
      }

      return id;
    },
    [clearTimer, dismiss],
  );

  useLayoutEffect(() => {
    return () => {
      Object.values(timeoutMap.current).forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutMap.current = {};
    };
  }, []);

  const value = useMemo<NotificationContextValue>(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-3">
        {notifications.map((notification) => (
          <NotificationCard key={notification.id} notification={notification} onDismiss={() => dismiss(notification.id)} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const useNotify = () => {
  const { notify, dismiss } = useNotifications();

  const notifyError = useCallback(
    (title: string, message?: string) =>
      notify({ type: 'error', title, message }),
    [notify],
  );

  const notifyWarning = useCallback(
    (title: string, message?: string) =>
      notify({ type: 'warning', title, message }),
    [notify],
  );

  const notifyInfo = useCallback(
    (title: string, message?: string, options?: { actionLabel?: string; onAction?: () => void; duration?: number }) =>
      notify({ type: 'info', title, message, actionLabel: options?.actionLabel, onAction: options?.onAction, duration: options?.duration }),
    [notify],
  );

  return { notify, dismiss, notifyError, notifyWarning, notifyInfo };
};

export default NotificationProvider;
